import { useState } from 'react'
import { sanitizeNumeric } from './input'
import { ProposalsApi } from '../../../api/proposals-api'
import { CreateBidDto } from '../../shared/types.ts'

export interface BidFormState {
  rate: string
  treasuryRate: string
  notes: string
}

export interface BidFieldErrors {
  rate?: string
  treasuryRate?: string
}

type Forms = Record<string, BidFormState>
type Errors = Record<string, BidFieldErrors>

export default function useBidForms() {
  const [forms, setForms] = useState<Forms>({})
  const [errors, setErrors] = useState<Errors>({})
  const [submitError, setSubmitError] = useState<string | null>(null)
  const isEditable = Object.keys(forms).length > 0

  const sanitizeChange = (
    scenarioId: string,
    field: keyof BidFormState,
    val: string
  ) => {
    const value =
      field === 'rate' || field === 'treasuryRate' ? sanitizeNumeric(val) : val
    setForms((prev) => ({
      ...prev,
      [scenarioId]: {
        ...prev[scenarioId],
        [field]: value,
      },
    }))
  }

  const addBid = (scenarioId: string) => {
    setForms((prev) => ({
      ...prev,
      [scenarioId]: { rate: '', treasuryRate: '', notes: '' },
    }))
  }

  const deleteBid = (scenarioId: string) => {
    setForms((prev) => {
      const copy = { ...prev }
      delete copy[scenarioId]
      return copy
    })
  }

  const validate = (): boolean => {
    if (Object.keys(forms).length === 0) {
      setSubmitError('At least one bid is required.')
      return false
    }
    const newErr: Errors = {}
    let invalid = false
    for (const [scenarioId, f] of Object.entries(forms)) {
      const e: BidFieldErrors = {}
      if (!f.rate) {
        e.rate = 'required'
        invalid = true
      }
      if (!f.treasuryRate) {
        e.treasuryRate = 'required'
        invalid = true
      }
      if (Object.keys(e).length) newErr[scenarioId] = e
    }
    setErrors(newErr)
    setSubmitError(invalid ? 'Form has errors' : null)
    return !invalid
  }

  const submit = async () => {
    if (!validate()) return false
    const dto: CreateBidDto[] = Object.entries(forms).map(
      ([scenarioId, f]) => ({
        scenarioId,
        rate: Number(f.rate),
        treasuryRate: Number(f.treasuryRate),
        notes: f.notes || null,
      })
    )

    setForms({})
    await ProposalsApi.createBids(dto)
    return true
  }

  return {
    forms,
    errors,
    submitError,
    isEditable,
    addBid,
    deleteBid,
    sanitizeChange,
    validate,
    submit,
  }
}
